import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  DatePicker,
  Typography,
  Divider,
  Checkbox,
  Radio,
  Alert,
} from 'antd';
import { FileOutlined, FolderOpenOutlined } from '@ant-design/icons';
import {
  DownloadMode,
  DirectoryGetDirectParams,
  DirectoryGetConfigParams,
} from '@core/@models/ApiManualModel';
import { ModeToggle } from '../shared/ModeToggle';
import { ConfigSelector } from '../shared/ConfigSelector';
import { DirectoryTree } from '../shared/DirectoryTree';
import { FileSelector } from '../shared/FileSelector';
import { CodeSnippetDisplay } from '../shared/CodeSnippetDisplay';

const { Text } = Typography;

const MAX_PATH_LENGTH = 500;

type DownloadMethod = 'single-file' | 'zip-with-structure';

export const GetTab: React.FC = () => {
  const [mode, setMode] = useState<DownloadMode>('direct');

  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [selectedPath, setSelectedPath] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [selectedFilesWithPaths, setSelectedFilesWithPaths] = useState<
    string[]
  >([]);
  const [effectiveFileCount, setEffectiveFileCount] = useState<number | null>(
    null
  );
  const [isPattern, setIsPattern] = useState<boolean>(false);
  const [beginDate, setBeginDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [includeSub, setIncludeSub] = useState<boolean>(false);
  const [downloadMethod, setDownloadMethod] = useState<DownloadMethod>(
    'zip-with-structure'
  );
  const [datePickerKey, setDatePickerKey] = useState(0);

  const [configName, setConfigName] = useState<string>('');

  useEffect(() => {
    if (isPattern) {
      setDownloadMethod('zip-with-structure');
    }
  }, [isPattern, selectedFile]);

  useEffect(() => {
    setSelectedFile('');
    setIsPattern(false);
  }, [downloadMethod]);

  useEffect(() => {
    setSelectedFolderId(null);
    setSelectedPath('');
    setSelectedFile('');
    setIsPattern(false);
    setBeginDate('');
    setEndDate('');
    setIncludeSub(false);
    setDownloadMethod('zip-with-structure');
    setConfigName('');
    setDatePickerKey((k) => k + 1);
  }, [mode]);

  const handleFolderSelect = (folderId: number | null, path: string) => {
    setSelectedFolderId(folderId);
    setSelectedPath(path);
    setSelectedFile('');
    setIsPattern(false);
  };

  const handleFileSelect = (
    filename: string,
    pattern: boolean,
    selectedKeys?: string[],
    effectiveCount?: number
  ) => {
    setSelectedFile(filename);
    setIsPattern(pattern);
    setSelectedFilesWithPaths(selectedKeys ?? []);
    setEffectiveFileCount(effectiveCount ?? null);
  };

  const handleDateChange = (dates: unknown) => {
    const range = dates as
      | [{ format: (s: string) => string }, { format: (s: string) => string }]
      | null;
    if (range && range[0] && range[1]) {
      setBeginDate(range[0].format('DD/MM/YYYY'));
      setEndDate(range[1].format('DD/MM/YYYY'));
    } else {
      setBeginDate('');
      setEndDate('');
    }
    setSelectedFile('');
    setIsPattern(false);
  };

  const effectivePathLength = (() => {
    if (downloadMethod === 'single-file' && !isPattern && selectedPath) {
      return `${selectedPath}/${selectedFile}`.length;
    }
    return selectedPath.length;
  })();
  const isPathTooLong = effectivePathLength > MAX_PATH_LENGTH;

  const isFormComplete = () => {
    if (mode === 'direct') {
      return selectedPath && selectedFile && !isPathTooLong;
    } else {
      return configName;
    }
  };

  const getEffectivePath = (): string => {
    if (downloadMethod === 'single-file' && !isPattern) {
      return selectedPath ? `${selectedPath}/${selectedFile}` : selectedFile;
    }
    return selectedPath;
  };

  const getEffectiveFileList = (): string => {
    if (downloadMethod === 'single-file' && !isPattern) {
      return '';
    }
    return selectedFile;
  };

  const getParams = (): DirectoryGetDirectParams | DirectoryGetConfigParams => {
    if (mode === 'direct') {
      return {
        mode: 'direct',
        directoryServicePath: getEffectivePath(),
        fileList: getEffectiveFileList(),
        isPattern,
        singleFile: downloadMethod === 'single-file' && !isPattern,
        beginDate: beginDate || undefined,
        endDate: endDate || undefined,
        includeSub,
      };
    } else {
      return {
        mode: 'config',
        configName,
        beginDate: beginDate || undefined,
        endDate: endDate || undefined,
      };
    }
  };

  const selectedFilesList = selectedFile ? selectedFile.split(',') : [];
  const folderName = selectedPath.split('/').pop() || selectedPath;
  const pathParts = selectedPath ? selectedPath.split('/') : [];

  const previewStructure = (() => {
    if (!includeSub || selectedFilesWithPaths.length === 0) return null;
    const normalizedBase = (selectedPath || '').replace(/\/$/, '');
    const bySubfolder = new Map<string, string[]>();
    for (const key of selectedFilesWithPaths) {
      if (key.includes('/')) {
        const afterBase = normalizedBase
          ? key.startsWith(normalizedBase + '/')
            ? key.slice(normalizedBase.length + 1)
            : key
          : key;
        const parts = afterBase.split('/');
        const file = parts.pop() || key;
        const subfolder = parts.join('/');
        if (!bySubfolder.has(subfolder)) bySubfolder.set(subfolder, []);
        const arr = bySubfolder.get(subfolder);
        if (arr) arr.push(file);
      } else {
        if (!bySubfolder.has('')) bySubfolder.set('', []);
        const rootArr = bySubfolder.get('');
        if (rootArr) rootArr.push(key);
      }
    }
    return bySubfolder;
  })();

  return (
    <div className="directory-get-tab">
      <Card size="small" className="info-card">
        <Text type="secondary">
          <strong>GET /external/get/directory-service/file</strong> - Download
          file(s) from directory service. Supports wildcard patterns (returns
          ZIP).
          <br />
          Limits:
          <br />• Directory path ≤{MAX_PATH_LENGTH} characters
          <br />
          • Each filename ≤255 characters
          <br />• File list ≤100 files
        </Text>
      </Card>

      <Divider />

      <ModeToggle mode={mode} onChange={setMode} />

      <Divider />

      <Form layout="vertical">
        {mode === 'config' ? (
          <>
            <Form.Item label="Saved Config" required>
              <ConfigSelector
                type="directory"
                value={configName}
                onChange={setConfigName}
                placeholder="Select a saved directory config"
              />
            </Form.Item>

            <Form.Item label="Date Range (Optional)">
              <DatePicker.RangePicker
                format="DD/MM/YYYY"
                onChange={handleDateChange}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </>
        ) : (
          <>
            {/* Step 1: Folder */}
            <Form.Item label="Folder" required>
              <DirectoryTree
                onSelect={handleFolderSelect}
                selectedPath={selectedPath}
              />
              {isPathTooLong && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                  message={`Path too long (${effectivePathLength}/${MAX_PATH_LENGTH} chars). Select a shorter path.`}
                />
              )}
            </Form.Item>

            {/* Step 2: Scope & Date Range - affects which files show */}
            {selectedPath && (
              <Form.Item label="Scope & Date Range">
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <DatePicker.RangePicker
                      key={`dir-${datePickerKey}`}
                      format="DD/MM/YYYY"
                      onChange={handleDateChange}
                      style={{ width: '100%' }}
                      placeholder={['Begin Date', 'End Date']}
                    />
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Filters files by created date
                      </Text>
                    </div>
                  </div>
                  <Checkbox
                    checked={includeSub}
                    onChange={(e) => {
                      setIncludeSub(e.target.checked);
                      setSelectedFile('');
                      setIsPattern(false);
                    }}
                    style={{ marginTop: 5 }}
                  >
                    Include subdirectories
                  </Checkbox>
                </div>
              </Form.Item>
            )}

            {/* Step 3: Download Method */}
            {selectedPath && (
              <Form.Item label="Download Method">
                <Radio.Group
                  value={downloadMethod}
                  onChange={(e) => setDownloadMethod(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="single-file">
                    <FileOutlined style={{ marginRight: 6 }} />
                    Single file
                  </Radio.Button>
                  <Radio.Button value="zip-with-structure">
                    <FolderOpenOutlined style={{ marginRight: 6 }} />
                    ZIP (with folder structure)
                  </Radio.Button>
                </Radio.Group>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {downloadMethod === 'single-file'
                      ? 'Returns the file directly without ZIP wrapper'
                      : 'Returns ZIP preserving directory hierarchy. Select multiple files or use a wildcard pattern.'}
                  </Text>
                </div>
              </Form.Item>
            )}

            {/* Step 4: File Selection */}
            {selectedPath && (
              <Form.Item label="File" required>
                <FileSelector
                  key={`${downloadMethod}-${includeSub}`}
                  folderId={selectedFolderId}
                  value={selectedFile}
                  onChange={handleFileSelect}
                  mode="get"
                  includeSub={includeSub}
                  folderPath={selectedPath}
                  multiple={downloadMethod === 'zip-with-structure'}
                  beginDate={beginDate}
                  endDate={endDate}
                />
              </Form.Item>
            )}

            {/* Step 5: Output Preview */}
            {selectedFile && (
              <Form.Item label="Output Preview">
                <div
                  style={{
                    padding: 12,
                    background: '#fafafa',
                    borderRadius: 4,
                    border: '1px solid #f0f0f0',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                >
                  {downloadMethod === 'single-file' ? (
                    <div>
                      <FileOutlined
                        style={{ marginRight: 6, color: '#1890ff' }}
                      />
                      {selectedFile}
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 11,
                          color: '#888',
                          fontFamily: 'inherit',
                        }}
                      >
                        Response: raw file content (
                        {selectedFile.split('.').pop() || 'binary'})
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ marginBottom: 4 }}>
                        <FolderOpenOutlined
                          style={{ marginRight: 6, color: '#faad14' }}
                        />
                        {folderName}.zip
                      </div>
                      <div style={{ paddingLeft: 8 }}>
                        {/* Current folder path */}
                        {pathParts.map((part, i) => (
                          <div key={i} style={{ paddingLeft: i * 16 }}>
                            <FolderOpenOutlined
                              style={{
                                marginRight: 6,
                                color: '#faad14',
                                fontSize: 11,
                              }}
                            />
                            <span style={{ fontSize: 11, color: '#666' }}>
                              {part}/
                            </span>
                          </div>
                        ))}
                        {/* Files - with subfolder structure when includeSub */}
                        {isPattern ? (
                          <div
                            style={{
                              paddingLeft: pathParts.length * 16,
                              color: '#888',
                              fontStyle: 'italic',
                              fontSize: 11,
                            }}
                          >
                            ... files matching &quot;{selectedFile}&quot;
                          </div>
                        ) : previewStructure ? (
                          <>
                            {Array.from(previewStructure.entries())
                              .sort(([a], [b]) =>
                                (a || '').localeCompare(b || '')
                              )
                              .map(([subfolder, files]) => (
                                <div key={subfolder || '__current__'}>
                                  {subfolder ? (
                                    <div
                                      style={{
                                        paddingLeft: pathParts.length * 16,
                                      }}
                                    >
                                      <FolderOpenOutlined
                                        style={{
                                          marginRight: 6,
                                          color: '#faad14',
                                          fontSize: 11,
                                        }}
                                      />
                                      <span
                                        style={{ fontSize: 11, color: '#666' }}
                                      >
                                        {subfolder}/
                                      </span>
                                    </div>
                                  ) : null}
                                  {files.map((f: string, i: number) => (
                                    <div
                                      key={i}
                                      style={{
                                        paddingLeft:
                                          (pathParts.length +
                                            (subfolder ? 1 : 0)) *
                                          16,
                                      }}
                                    >
                                      <FileOutlined
                                        style={{
                                          marginRight: 6,
                                          color: '#1890ff',
                                          fontSize: 11,
                                        }}
                                      />
                                      <span style={{ fontSize: 11 }}>{f}</span>
                                    </div>
                                  ))}
                                </div>
                              ))}
                          </>
                        ) : (
                          selectedFilesList.map((f, i) => (
                            <div
                              key={i}
                              style={{ paddingLeft: pathParts.length * 16 }}
                            >
                              <FileOutlined
                                style={{
                                  marginRight: 6,
                                  color: '#1890ff',
                                  fontSize: 11,
                                }}
                              />
                              <span style={{ fontSize: 11 }}>{f.trim()}</span>
                            </div>
                          ))
                        )}
                        {/* Subfolder files when includeSub */}
                        {includeSub && isPattern && (
                          <div
                            style={{
                              paddingLeft: pathParts.length * 16,
                              color: '#888',
                              fontStyle: 'italic',
                              fontSize: 11,
                              marginTop: 2,
                            }}
                          >
                            ... + matching files from subdirectories
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 11,
                          color: '#888',
                          fontFamily: 'inherit',
                        }}
                      >
                        Response: ZIP file
                        {!isPattern &&
                          selectedFilesList.length > 0 &&
                          ` (${
                            effectiveFileCount != null && includeSub
                              ? effectiveFileCount
                              : selectedFilesList.length
                          } ${
                            (effectiveFileCount ?? selectedFilesList.length) ===
                            1
                              ? 'file'
                              : 'files'
                          })`}
                        {includeSub && ' · includes subdirectories'}
                      </div>
                    </div>
                  )}
                </div>
              </Form.Item>
            )}
          </>
        )}
      </Form>

      <Divider />

      <CodeSnippetDisplay
        apiType={
          mode === 'direct' ? 'directory-get-direct' : 'directory-get-config'
        }
        params={getParams()}
        disabled={!isFormComplete()}
      />
    </div>
  );
};
